import styled from '@emotion/styled';
import {
  EmailRounded,
  ErrorOutlineRounded,
  LockRounded,
  LoginRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { FormEvent, useState } from 'react';
import ThemeToggle from '~/modules/Elements/ThemeToggle';
import { useAuth } from '../AuthContext';

export default function LoginPage() {
  const { login } = useAuth();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const trimmedUser = user.trim();
    if (!trimmedUser || !password) {
      setErrorMessage('Por favor ingresa tu usuario o correo y contraseña.');
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await login({
        user: trimmedUser,
        password,
      });

      if (!result.success) {
        setErrorMessage(result.error || 'Credenciales inválidas. Por favor intenta de nuevo.');
      }
    } catch {
      setErrorMessage('Error de red. No se pudo conectar con el servidor.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <TopBar>
        <ThemeToggle />
      </TopBar>

      <CenterBox>
        <LoginCard>
          <LogoArea>
            <LogoImage src="/logo-128.png" alt="WalkyCam Logo" />
            <BrandTitle>WalkyCam</BrandTitle>
            <BrandSubtitle>Ingresa tus credenciales para acceder a la plataforma</BrandSubtitle>
          </LogoArea>

          {errorMessage && (
            <ErrorBanner role="alert">
              <ErrorOutlineRounded fontSize="small" />
              <span>{errorMessage}</span>
            </ErrorBanner>
          )}

          <Form onSubmit={handleSubmit} noValidate>
            <FormGroup>
              <Label htmlFor="login-username">Usuario o Correo electrónico</Label>
              <InputWrapper>
                <IconSlot>
                  <EmailRounded fontSize="small" />
                </IconSlot>
                <Input
                  id="login-username"
                  type="text"
                  name="username"
                  autoComplete="username"
                  placeholder="ej. usuario@walkycam.com"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  disabled={isSubmitting}
                  autoFocus
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="login-password">Contraseña</Label>
              <InputWrapper>
                <IconSlot>
                  <LockRounded fontSize="small" />
                </IconSlot>
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}>
                  {showPassword ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
                </PasswordToggleButton>
              </InputWrapper>
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner aria-hidden="true" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <LoginRounded fontSize="small" />
                  <span>Entrar a la plataforma</span>
                </>
              )}
            </SubmitButton>
          </Form>

          <CardFooter>
            <FooterNote>
              El acceso es obligatorio y requiere una cuenta activa en <strong>WalkyCam</strong>.
            </FooterNote>
          </CardFooter>
        </LoginCard>
      </CenterBox>
    </PageWrapper>
  );
}

const PageWrapper = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--walky-canvas);
  color: var(--walky-ink);
  position: relative;
  overflow: auto;
  border-top: 4px solid var(--walky-orange);
`;

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
`;

const CenterBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px 48px;
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: var(--walky-surface);
  border: 1px solid var(--walky-border);
  border-radius: 16px;
  box-shadow: var(--walky-shadow-strong);
  padding: 36px 32px;
  box-sizing: border-box;
  animation: cardFadeIn 0.35s ease-out;

  @keyframes cardFadeIn {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 480px) {
    padding: 28px 20px;
    border-radius: 12px;
  }
`;

const LogoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 24px;
`;

const LogoImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 14px;
  object-fit: cover;
  box-shadow: 0 8px 20px rgba(244, 90, 10, 0.25);
  margin-bottom: 12px;
`;

const BrandTitle = styled.h1`
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  color: var(--walky-ink);
  letter-spacing: -0.02em;
`;

const BrandSubtitle = styled.p`
  font-size: 13px;
  color: var(--walky-muted);
  margin: 6px 0 0 0;
  line-height: 1.4;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--walky-error-bg);
  border: 1px solid var(--walky-error-border);
  color: var(--walky-error-text);
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 20px;
  animation: shake 0.3s ease-in-out;

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    20%,
    60% {
      transform: translateX(-4px);
    }
    40%,
    80% {
      transform: translateX(4px);
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--walky-muted);
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const IconSlot = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--walky-muted);
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 40px 12px 38px;
  background: var(--walky-canvas);
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--walky-ink);
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::placeholder {
    color: var(--walky-muted);
    opacity: 0.6;
    font-weight: 400;
  }

  &:focus {
    border-color: var(--walky-orange);
    box-shadow: 0 0 0 3px rgba(244, 90, 10, 0.18);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: var(--walky-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--walky-ink);
  }
`;

const SubmitButton = styled.button`
  margin-top: 6px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, var(--walky-orange), #ff7524);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 13px 18px;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(244, 90, 10, 0.35);
  transition:
    transform 0.1s ease,
    filter 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
    box-shadow: 0 8px 24px rgba(244, 90, 10, 0.42);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    filter: grayscale(0.2);
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CardFooter = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--walky-border);
  text-align: center;
`;

const FooterNote = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--walky-muted);
  line-height: 1.45;

  strong {
    color: var(--walky-ink);
  }
`;
